'use client';

/**
 * Verify-your-email banner.
 *
 * Shows under the top nav whenever the current user has
 * `is_email_verified === false`.  Per users-me.json:frontend_usage and
 * auth-register.json:frontend_usage email verification is intentionally
 * non-blocking — the user can still use the app, but we nudge until they
 * click the link (or hit the resend button here).
 *
 * Resend is rate-limited to 3 / hour / IP server-side
 * (auth-verify-email.json), and the endpoint always returns 200 to avoid
 * leaking whether the email exists, so on click we just toast "Sent" and
 * disable the button briefly.
 */
import { Mail } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ui/Toast';
import { ApiError, RateLimitError } from '@/lib/api';
import type { UserMe } from '@/lib/auth';
import { resendVerificationEmail } from '@/lib/auth';

interface VerifyEmailBannerProps {
  user: UserMe;
}

export function VerifyEmailBanner({ user }: VerifyEmailBannerProps) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (user.is_email_verified) return null;

  async function onResend(): Promise<void> {
    if (busy || sent) return;
    setBusy(true);
    try {
      await resendVerificationEmail(user.email);
      setSent(true);
      toast.success('Verification email sent', {
        description: `Check ${user.email} — link expires in 7 days.`,
      });
    } catch (err) {
      if (err instanceof RateLimitError) {
        toast.error('Too many requests', {
          description: 'Try again in 60 minutes.',
        });
      } else {
        toast.error("Couldn't send the email", {
          description: err instanceof ApiError ? err.message : 'Please try again.',
        });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="border-warning-600/30 flex items-center justify-between gap-3 border-b bg-warning-50 px-4 py-2 text-sm text-warning-900"
      role="status"
    >
      <div className="flex items-center gap-2">
        <Mail aria-hidden size={16} strokeWidth={1.75} />
        <span>
          Verify your email — check the inbox for{' '}
          <strong className="font-medium">{user.email}</strong>.
        </span>
      </div>
      <button
        className="text-sm font-medium text-warning-900 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
        disabled={busy || sent}
        type="button"
        onClick={onResend}
      >
        {sent ? 'Email sent' : busy ? 'Sending…' : 'Resend email'}
      </button>
    </div>
  );
}
